import { useQuery } from '@tanstack/react-query';
import { ReactNode, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { AdminClient, isNil } from '@activepieces/shared';

import { adminApi } from './admin-api';
import { ConsentWarningDialog } from './consent-denied-dialog';

const CLIENT_STALE_TIME_MS = 5 * 60 * 1000;

type AdminConsentGate = {
  canLoadCustomerData: boolean;
  isClientError: boolean;
  consentDialog: ReactNode;
};

/**
 * Customer data may only be fetched once the client is known: until then,
 * whether consent is required is unknown, so the gate stays closed.
 */
export const useAdminConsentGate = (
  tenantId: string | undefined,
): AdminConsentGate => {
  const navigate = useNavigate();
  const [consentAccepted, setConsentAccepted] = useState(false);

  const { data: client, isError: isClientError } = useQuery<AdminClient>({
    queryKey: ['admin-client', tenantId],
    queryFn: () => adminApi.getClient(tenantId!),
    enabled: !isNil(tenantId),
    staleTime: CLIENT_STALE_TIME_MS,
  });

  const needsConsent =
    !isNil(client) && !client.allowConsent && !consentAccepted;

  return {
    canLoadCustomerData: !isNil(client) && !needsConsent,
    isClientError,
    consentDialog: needsConsent ? (
      <ConsentWarningDialog
        open={true}
        onCancel={() => navigate(-1)}
        onProceed={() => setConsentAccepted(true)}
      />
    ) : null,
  };
};
